import {
  Button,
  IconButton,
  Input,
  Stack,
  Title,
  useTheme,
  useToast
} from "@introist/react-foundation/v2";
import { useRef } from "react";
import { JsonEditor } from "../templates/CodeEditor/CodeEditor";
import {
  describeFormat,
  emptyTestCaseJson,
  newTestCase,
  parseUploadedTestCases,
  TestCase,
  TestCaseFormat,
  TransformTestResult
} from "./testcases";

type TestCasesPaneProps = {
  format: TestCaseFormat;
  testCases: TestCase[];
  onChange: (testCases: TestCase[]) => void;
  results: Record<string, TransformTestResult>;
  onRun: () => Promise<unknown>;
  disabled?: boolean;
};

export const TestCasesPane = ({
  format,
  testCases,
  onChange,
  results,
  onRun,
  disabled
}: TestCasesPaneProps) => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const update = (id: string, updates: Partial<TestCase>) =>
    onChange(testCases.map(t => (t.id === id ? { ...t, ...updates } : t)));

  const onUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const uploaded = parseUploadedTestCases(format, await file.text(), file.name);
      onChange([...testCases, ...uploaded]);
    } catch (e: any) {
      toast.error(`Could not read test data: ${e.message}`);
    }
  };

  return (
    <Stack
      vertical
      gap="small"
      style={{ padding: "var(--spacing-small)", boxSizing: "border-box", height: "100%" }}
    >
      <Stack justifyContent="space-between" style={{ width: "100%" }}>
        <Title>Test cases</Title>
        <Stack gap="xSmall">
          <Button
            size="small"
            variant="blended"
            startIcon="plus"
            disabled={disabled}
            onClick={() =>
              onChange([
                ...testCases,
                newTestCase(`Case ${testCases.length + 1}`, emptyTestCaseJson(format))
              ])
            }
          >
            Add
          </Button>
          <Button
            size="small"
            variant="blended"
            disabled={disabled}
            onClick={() => fileRef.current?.click()}
          >
            Upload JSON
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon="playCircle"
            disabled={disabled || testCases.length === 0}
            onClickWithLoading={onRun}
          >
            Run all
          </Button>
        </Stack>
      </Stack>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        style={{ display: "none" }}
        onChange={onUpload}
      />
      <Stack vertical gap="medium" style={{ overflowY: "auto", width: "100%", flex: 1 }}>
        {testCases.length === 0 && (
          <Title style={{ margin: "auto" }}>
            Add a test case with {describeFormat(format)} data, or upload a JSON file
          </Title>
        )}
        {testCases.map(testCase => (
          <Stack key={testCase.id} vertical gap="xSmall" style={{ width: "100%" }}>
            <Stack gap="xSmall" style={{ width: "100%" }}>
              <Input
                size="small"
                value={testCase.name}
                onChange={name => update(testCase.id, { name })}
                style={{ flex: 1 }}
              />
              <IconButton
                icon="trash"
                disabled={disabled}
                onClick={() => onChange(testCases.filter(t => t.id !== testCase.id))}
              />
            </Stack>
            <JsonEditor
              value={testCase.json}
              onChange={json => update(testCase.id, { json })}
              readOnly={disabled}
              maxHeight="200px"
            />
            <TestResult result={results[testCase.id]} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const TestResult = ({ result }: { result?: TransformTestResult }) => {
  const { theme } = useTheme();
  if (!result) return null;

  const summary = !result.success
    ? `✗ ${result.error ?? "Failed"}`
    : result.unchanged
    ? "✓ undefined (field left unchanged)"
    : result.value === null
    ? "✓ null (field cleared)"
    : `✓ "${result.value}"`;

  return (
    <Stack vertical gap="xSmall">
      <Title
        variant="bold"
        style={{
          color: result.success ? theme.palette.success.default : theme.palette.danger.default
        }}
      >
        {summary}
      </Title>
      {result.logs.map((log, i) => (
        <Title key={i} style={{ fontFamily: "monospace" }}>
          {log}
        </Title>
      ))}
    </Stack>
  );
};
